import {z} from 'zod';
import {
  DRAFT_LIMITS, ResourceAsset, ResourceWork, ResourceMetadata, ResourceMetadataUpdate,
  DraftContent, DraftSnapshot, DraftSummary, DraftReplace, DraftDelete, DraftDeleted, DraftRestore,
  LibraryAssetsQuery, LibraryWorksQuery, DraftsQuery, cursorPage,
} from './resources.js';

export const CreationResolveCommand = z.discriminatedUnion('operation',[
  z.object({operation:z.literal('library.metadata'),command:ResourceMetadataUpdate}).strict(),
  z.object({operation:z.literal('draft.create'),command:DraftContent}).strict(),
  z.object({operation:z.literal('draft.replace'),id:z.string().uuid(),command:DraftReplace}).strict(),
  z.object({operation:z.literal('draft.delete'),id:z.string().uuid(),command:DraftDelete}).strict(),
]);
export const CreationCommitted = z.discriminatedUnion('operation',[
  z.object({state:z.literal('committed'),operation:z.literal('library.metadata'),value:ResourceMetadata}).strict(),
  z.object({state:z.literal('committed'),operation:z.literal('draft.create'),value:DraftSnapshot}).strict(),
  z.object({state:z.literal('committed'),operation:z.literal('draft.replace'),value:DraftSnapshot}).strict(),
  z.object({state:z.literal('committed'),operation:z.literal('draft.delete'),value:DraftDeleted}).strict(),
]);
export const CreationResolution = z.union([
  z.object({state:z.literal('outcome_unknown')}).strict(),CreationCommitted,
]);
export const CreationConflictEnvelope = z.object({error:z.object({
  code:z.literal('REVISION_CONFLICT'),message:z.string(),request_id:z.string().uuid(),retryable:z.literal(false),
  current:z.union([ResourceMetadata,DraftSnapshot,z.null()]),
}).strict()}).strict();
export const CreationAssetPage=cursorPage(ResourceAsset);
export const CreationWorkPage=cursorPage(ResourceWork);
export const CreationDraftPage=cursorPage(DraftSummary);
export const creationHttpSchemas={CreationResolveCommand,CreationResolution,CreationConflictEnvelope,
  CreationAssetPage,CreationWorkPage,CreationDraftPage,ResourceMetadata,ResourceMetadataUpdate,
  DraftContent,DraftSnapshot,DraftReplace,DraftDelete,DraftDeleted,DraftRestore,
  LibraryAssetsQuery,LibraryWorksQuery,DraftsQuery};

// Exact paths only; neither trailing segments nor unsupported methods gain a larger allowance.
export function creationRoute(pathname:string):{kind:'assets'|'works'|'metadata'|'drafts'|'draft'|'resolve';allow:string;id?:string}|undefined {
  switch(pathname){
    case '/api/v1/library/assets':return {kind:'assets',allow:'GET'};
    case '/api/v1/library/works':return {kind:'works',allow:'GET'};
    case '/api/v1/library/metadata':return {kind:'metadata',allow:'PUT'};
    case '/api/v1/creation/drafts':return {kind:'drafts',allow:'GET, POST'};
    case '/api/v1/creation/mutations/resolve':return {kind:'resolve',allow:'POST'};
  }
  const match=/^\/api\/v1\/creation\/drafts\/([^/]+)$/.exec(pathname);
  return match?{kind:'draft',allow:'GET, PUT, DELETE',id:match[1]}:undefined;
}
const emptyCreationQuery=z.object({}).strict();
// Read the original URLSearchParams before any framework converts it through an object.
export function creationQueryValues(search:URLSearchParams):Record<string,unknown> {
  const result:Record<string,unknown>=Object.create(null);
  const invalid=()=>new z.ZodError([{code:'custom',path:[],message:'Invalid creation query'}]);
  for(const [name,value] of search){
    if(Object.hasOwn(result,name))throw invalid();
    if(name==='limit'){
      if(!/^[1-9][0-9]{0,2}$/.test(value))throw invalid();result[name]=Number(value);
    }else if(name==='favorite'){
      if(value!=='true'&&value!=='false')throw invalid();result[name]=value==='true';
    }else result[name]=value;
  }
  return result;
}
// Unsupported methods retain the application's method/realm decision. Other paths are untouched.
export function validateCreationQuery(method:string,pathname:string,search:URLSearchParams):void {
  const route=creationRoute(pathname);
  if(!route||!route.allow.split(', ').includes(method))return;
  const schema=method==='GET'?(route.kind==='assets'?LibraryAssetsQuery:
    route.kind==='works'?LibraryWorksQuery:route.kind==='drafts'?DraftsQuery:emptyCreationQuery):emptyCreationQuery;
  schema.parse(creationQueryValues(search));
}
export function creationRequestByteLimit(method:string,pathname:string):number {
  if(method==='POST'&&pathname==='/api/v1/media/video/quotes')return 64*1024;
  if(method==='PUT'&&/^\/api\/v1\/media\/video\/upload-intents\/[a-f0-9-]{36}\/content$/.test(pathname))return 64*1024*1024;
  const route=creationRoute(pathname);
  if((method==='POST'&&(route?.kind==='drafts'||route?.kind==='resolve'))||
    (method==='PUT'&&route?.kind==='draft'&&z.string().uuid().safeParse(route.id).success))return DRAFT_LIMITS.requestBytes;
  if(method==='PUT'&&/^\/api\/v1\/upload-intents\/[a-f0-9-]{36}\/content$/.test(pathname))return 8*1024*1024;
  return 16*1024;
}
const errors=[400,401,403,404,405,409,413,429,503] as const;
const read={security:['session'],errors} as const;
const write={...read,csrf:true,idempotency:true} as const;
export const creationHttpEndpoints={
  '/api/v1/library/assets':{get:{...read,operationId:'creationAssets',querySchema:'LibraryAssetsQuery',responseSchema:'CreationAssetPage'}},
  '/api/v1/library/works':{get:{...read,operationId:'creationWorks',querySchema:'LibraryWorksQuery',responseSchema:'CreationWorkPage'}},
  '/api/v1/library/metadata':{put:{...write,operationId:'creationMetadata',requestSchema:'ResourceMetadataUpdate',responseSchema:'ResourceMetadata'}},
  '/api/v1/creation/drafts':{
    get:{...read,operationId:'creationDrafts',querySchema:'DraftsQuery',responseSchema:'CreationDraftPage'},
    post:{...write,operationId:'creationDraftCreate',requestSchema:'DraftContent',responseSchema:'DraftSnapshot',status:201}},
  '/api/v1/creation/drafts/{id}':{
    get:{...read,operationId:'creationDraftGet',responseSchema:'DraftRestore'},
    put:{...write,operationId:'creationDraftReplace',requestSchema:'DraftReplace',responseSchema:'DraftSnapshot'},
    delete:{...write,operationId:'creationDraftDelete',requestSchema:'DraftDelete',responseSchema:'DraftDeleted'}},
  '/api/v1/creation/mutations/resolve':{post:{...write,operationId:'creationMutationResolve',requestSchema:'CreationResolveCommand',responseSchema:'CreationResolution'}},
} as const;
